import { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import Swiper from 'swiper';
import { Swiper as Slider, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode, A11y } from 'swiper/modules';
import { DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import { ImageProp } from '../../models/image';
import { Image } from './picture';
import { CloseSVG } from '../styled/Close';
import ArrowSVG from '../../public/img/thin-arrow.svg';
import videoCoverImg from '../../public/img/slider-video-placeholder.png';
import 'swiper/css';
import 'swiper/css/navigation';

export function PictureModal({
  videoInfo,
  images,
  imageModal,
  scrollY,
  setVisible,
}: {
  videoInfo?: {
    video: string;
    cover: string;
  };
  images: ImageProp[];
  imageModal: { slide: number | null; visible: boolean };
  scrollY?: number;
  setVisible: Dispatch<
    SetStateAction<{
      slide: number | null;
      visible: boolean;
    }>
  >;
}) {
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
  const mainSlider = useRef<{ swiper: Swiper } | null>(null);

  const prevSlide = () => {
    if (mainSlider.current) {
      mainSlider.current.swiper.slidePrev();
    }
  };

  const nextSlide = () => {
    if (mainSlider.current) {
      mainSlider.current.swiper.slideNext();
    }
  };

  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
  }, []);

  return (
    <div
      css={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: '#fff',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        [DEVICE_SCREENS_MAX_STRING.sm]: {
          justifyContent: 'space-between',
        },
      }}
    >
      <div>
        <div
          css={{
            height: 70,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '0 30px',
          }}
        >
          <CloseSVG
            css={{ cursor: 'pointer' }}
            onClick={() => {
              setVisible({ slide: null, visible: false });
              document.documentElement.style.overflow = 'unset';
              if (scrollY) window.scrollTo(0, scrollY);
            }}
          />
        </div>

        <Slider
          ref={mainSlider}
          modules={[FreeMode, Navigation, Thumbs, A11y]}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          css={{
            width: '100%',
            height: 'calc(100vh - 210px)',
            '.swiper-slide': {
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            },
            [DEVICE_SCREENS_MAX_STRING.lg]: {
              height: 'calc(100vh - 180px)',
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              height: 'calc(100vh - 200px)',
            },
          }}
          initialSlide={imageModal.slide || 0}
          loop
        >
          {videoInfo?.video && (
            <SwiperSlide className='swiper-slide' key='video-slider'>
              <div
                dangerouslySetInnerHTML={{ __html: videoInfo.video }}
                css={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 210px)',
                  margin: '0 auto',
                  background: 'white',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    height: '300px',
                  },
                  iframe: {
                    width: '100%',
                    height: '100%',
                  },
                }}
              />
            </SwiperSlide>
          )}
          {images.map((image: ImageProp) => (
            <SwiperSlide className='swiper-slide' key={image.id}>
              <Image
                image={image}
                css={{
                  width: 'auto',
                  height: '100%',
                  objectFit: 'contain',
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 210px)',
                  margin: '0 auto',
                  userSelect: 'none',
                  zIndex: 2,
                }}
              />
              <Image
                image={image}
                css={{
                  width: '100%',
                  height: '100%',
                  filter: 'blur(30px)',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  userSelect: 'none',
                  objectFit: 'cover',
                }}
              />
            </SwiperSlide>
          ))}
        </Slider>
        <button
          type='button'
          onClick={prevSlide}
          css={{
            width: 200,
            height: 'calc(100vh - 210px)',
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            position: 'absolute',
            top: 70,
            left: 0,
            zIndex: 100,
            opacity: 0.2,
            paddingLeft: 8,
            transition: '.3s',
            ':hover': {
              opacity: 0.6,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              width: 50,
              height: 'calc(100vh - 135px)',
            },
          }}
        >
          <ArrowSVG css={{ width: 46, height: 46 }} />
        </button>
        <button
          type='button'
          onClick={nextSlide}
          css={{
            width: 200,
            height: 'calc(100vh - 210px)',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            position: 'absolute',
            top: 70,
            right: 0,
            zIndex: 100,
            opacity: 0.2,
            paddingRight: 8,
            transition: '.3s',
            ':hover': {
              opacity: 0.6,
            },
            [DEVICE_SCREENS_MAX_STRING.md]: {
              width: 50,
              height: 'calc(100vh - 135px)',
            },
          }}
        >
          <ArrowSVG
            css={{ width: 40, height: 40, transform: 'rotate(180deg)' }}
          />
        </button>
      </div>

      <div>
        <Slider
          modules={[FreeMode, Navigation, Thumbs]}
          onSwiper={(slider) => setThumbsSwiper(slider)}
          slidesPerView='auto'
          watchSlidesProgress
          centerInsufficientSlides
          css={{
            '.swiper-slide': {
              width: 80,
              height: 80,
              transition: '.7s ease',
              margin: '30px 15px',
            },
            '.swiper-slide .preview': {
              height: '100%',
            },
            '.swiper-slide-thumb-active .preview': {
              transform: 'scale(1.2)',
            },
            [DEVICE_SCREENS_MAX_STRING.lg]: {
              '.swiper-slide': {
                width: 72,
                height: 72,
              },
            },
            [DEVICE_SCREENS_MAX_STRING.sm]: {
              '.swiper-slide': {
                margin: 15,
                width: 38,
                height: 38,
              },
            },
          }}
        >
          {videoInfo?.video && (
            <SwiperSlide key='video'>
              <div
                className='preview'
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    padding: 15,
                  },
                }}
              >
                <Image
                  image={videoInfo.cover}
                  css={{
                    width: '100%',
                    height: '100%',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    cursor: 'pointer',
                    position: 'absolute',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    boxShadow: '0px 0px 15px 0 rgba(0, 0, 0, 0.3)',
                    transition: '.7s',
                  }}
                />
                <Image
                  css={{
                    width: '100%',
                    height: '100%',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    position: 'absolute',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    zIndex: 2,
                  }}
                  image={videoCoverImg.src}
                />
              </div>
            </SwiperSlide>
          )}
          {images.map((image: ImageProp) => (
            <SwiperSlide key={image.id}>
              <div
                className='preview'
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                  [DEVICE_SCREENS_MAX_STRING.sm]: {
                    padding: 15,
                  },
                }}
              >
                <Image
                  image={image}
                  css={{
                    width: '100%',
                    height: '100%',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    position: 'absolute',
                    cursor: 'pointer',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    boxShadow: '0px 0px 15px 0 rgba(0, 0, 0, 0.3)',
                    transition: '.7s',
                  }}
                />
              </div>
            </SwiperSlide>
          ))}
        </Slider>
      </div>
    </div>
  );
}
